import React from 'react'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import moment from 'moment/moment'
import IconButton from '@material-ui/core/IconButton'
import { CancelOutlined } from '@material-ui/icons'
import { ProjectsTableContainer, StyledTableHead } from '../Approvals/ProjectsView.styles'
import { StyledMutedButton } from '../Approvals/ApprovalView.styles'
import { splitExpense, updateExpense } from '../../entities/expenses/service'
import SplitExpensesDialog from './SplitExpenses'

const ExpensesTable = ({ expenses, people, isStaff, onUpdateExpense, client, onSplitExpense }) => {
  const [expenseToSplit, setExpenseToSplit] = React.useState(null)

  const handleDeferExpense = async expense => {
    let deferToMonth
    const isDeferred = !!expense.deferToMonth

    if (isDeferred) {
      const momentDefer = moment.utc(expense.deferToMonth)
      if (momentDefer.isSame(moment.utc(), 'month')) {
        deferToMonth = moment.utc().add(1, 'month')
      } else if (momentDefer.isAfter(moment.utc(), 'month')) {
        deferToMonth = moment.utc()
      }
    } else {
      deferToMonth = moment.utc().add(1, 'month')
    }
    const expenseToUpdate = {
      ...expense,
      deferToMonth: deferToMonth ? deferToMonth.toISOString() : null,
    }
    await updateExpense(client, expense._id, expenseToUpdate)

    onUpdateExpense(expense._id, {
      deferToMonth: deferToMonth ? deferToMonth.toISOString() : null,
    })
  }

  const handleRemoveDefer = async expense => {
    const currentMonth = moment
      .utc()
      .startOf('month')
      .toISOString()
    const expenseToUpdate = {
      ...expense,
      deferToMonth: currentMonth,
    }
    await updateExpense(client, expense._id, expenseToUpdate)
    onUpdateExpense(expense._id, { deferToMonth: currentMonth })
  }

  const handleWriteOff = async expense => {
    if (isDeferedToAnotherMonth(expense)) return
    const expenseToUpdate = {
      ...expense,
      status: 'WRITEOFF',
    }
    await updateExpense(client, expense._id, expenseToUpdate)
    if (onUpdateExpense) onUpdateExpense(expense._id, { status: 'WRITEOFF' })
  }

  const handleUndoWriteOff = async expense => {
    const expenseToUpdate = {
      ...expense,
      status: 'RAW',
    }
    await updateExpense(client, expense._id, expenseToUpdate)
    if (onUpdateExpense) onUpdateExpense(expense._id, { status: 'RAW' })
  }

  const isDeferedToAnotherMonth = expense => {
    if (!expense.deferToMonth) return false
    const momentDefer = moment.utc(expense.deferToMonth)
    return momentDefer.isAfter(moment.utc(), 'month')
  }

  return (
    <TableContainer component={ProjectsTableContainer} rows={5}>
      <Table aria-label="simple table">
        <StyledTableHead>
          <TableRow>
            <TableCell align="left">Staff</TableCell>
            <TableCell align="left">Description</TableCell>
            <TableCell align="left">Category</TableCell>
            <TableCell align="left">Cost</TableCell>
            <TableCell align="left">Date</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {expenses &&
            expenses.map(expense => {
              const deferred = isDeferedToAnotherMonth(expense)
              const isWriteOff = expense.status === 'WRITEOFF'
              return (
                <TableRow key={expense._id}>
                  <TableCell align="left">
                    {people[expense.personId]?.fullName || expense.account}
                  </TableCell>
                  <TableCell align="left">{expense.memo}</TableCell>
                  <TableCell align="left">{expense.category}</TableCell>
                  <TableCell align="left">{expense.amount}</TableCell>
                  <TableCell align="center">
                    {moment.utc(expense.date).format('MM/DD/YYYY')}
                  </TableCell>
                  <TableCell align="center">
                    {deferred && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <IconButton onClick={() => handleRemoveDefer(expense)}>
                          <CancelOutlined />
                        </IconButton>
                        <span>
                          Deferred to {moment.utc(expense.deferToMonth).format('MMMM - YYYY')}
                        </span>
                      </div>
                    )}
                    {isWriteOff && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <IconButton onClick={() => handleUndoWriteOff(expense)}>
                          <CancelOutlined />
                        </IconButton>
                        <span>Write Off</span>
                      </div>
                    )}
                    {!deferred && !isWriteOff && (
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                        <StyledMutedButton onClick={() => handleDeferExpense(expense)}>
                          Defer
                        </StyledMutedButton>
                        {isStaff && (
                          <>
                            <StyledMutedButton onClick={() => handleWriteOff(expense)}>
                              Write Off
                            </StyledMutedButton>
                            <StyledMutedButton onClick={() => setExpenseToSplit(expense)}>
                              Split
                            </StyledMutedButton>
                          </>
                        )}
                      </div>
                    )}
                  </TableCell>

                  {expenseToSplit && (
                    <SplitExpensesDialog
                      expenseToSplit={expenseToSplit}
                      open={!!expenseToSplit}
                      onAccept={async (expense, splitExpenses, actions) => {
                        const amounts = splitExpenses.map(e => Number(e.amount))
                        const statusses = actions.map(action => {
                          if (action === 'writeoff') return 'WRITEOFF'
                          if (action === 'defer') return expense.status
                          return expense.status
                        })

                        const splitResult = await splitExpense({
                          client,
                          _id: expense._id,
                          amounts,
                          statusses,
                        })

                        onSplitExpense(splitResult)

                        await Promise.all(
                          actions.map(async (action, idx) => {
                            if (action === 'defer') {
                              const expenseToDefer =
                                idx === 0 ? splitResult.parent : splitResult.child
                              await handleDeferExpense(expenseToDefer)
                            }
                          }),
                        )

                        setExpenseToSplit(null)
                      }}
                      onCancel={() => setExpenseToSplit(null)}
                    />
                  )}
                </TableRow>
              )
            })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default ExpensesTable
